import React, { useEffect, useRef, useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import './index.css'

const quickPrompts = [
  'Who has an unconfirmed lesson today?',
  'Which invoices are 15+ days overdue?',
  "Summarize Diego Martino's plan progress",
  'Draft a reschedule message to a parent'
]

const initialMessages = [
  {
    id: 'assistant-welcome',
    role: 'assistant',
    text: 'Hi! I am your AI calendar assistant. Ask me about scheduling, conflicts, follow-ups, or upcoming lessons.'
  }
]

const AI_ACTION_MUTATION = gql`
  mutation aiAction($message: String!, $conversationId: ID) {
    aiAction(message: $message, conversationId: $conversationId)
  }
`

const parseAiActionResponse = (response) => {
  if (!response || typeof response !== 'string') {
    return { text: '' }
  }

  try {
    const parsedResponse = JSON.parse(response)

    if (typeof parsedResponse === 'string') {
      return { text: parsedResponse }
    }

    if (parsedResponse && typeof parsedResponse === 'object') {
      const actionLabel = typeof parsedResponse.action === 'string'
        ? parsedResponse.action
        : parsedResponse.action?.label || parsedResponse.actionLabel || parsedResponse.cta || null

      return {
        text: parsedResponse.reply || parsedResponse.message || parsedResponse.text || '',
        conversationId: parsedResponse.conversationId || null,
        actionLabel
      }
    }
  } catch (error) {
    return { text: response }
  }

  return { text: response }
}

const AssistantSparkIcon = ({ className }) => (
  <svg className={className} viewBox='0 0 20 20' fill='currentColor' aria-hidden='true'>
    <path d='M10 1.7 L11.84 8.16 L18.3 10 L11.84 11.84 L10 18.3 L8.16 11.84 L1.7 10 L8.16 8.16 Z' />
  </svg>
)

const CloseIcon = ({ className }) => (
  <svg
    className={className}
    viewBox='0 0 20 20'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    aria-hidden='true'
  >
    <path d='M5 5l10 10M15 5 5 15' />
  </svg>
)

const NewChatIcon = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.8'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' />
    <path d='M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z' />
  </svg>
)

const SendIcon = () => (
  <svg viewBox='0 0 20 20' fill='currentColor' aria-hidden='true'>
    <path d='M2.5 2.5 17.8 9.6a.6.6 0 0 1 0 1.1L2.5 17.8a.6.6 0 0 1-.85-.68L4 10 1.65 3.18a.6.6 0 0 1 .85-.68Z' />
  </svg>
)

const AIChat = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState(initialMessages)
  const [isThinking, setIsThinking] = useState(false)
  const [conversationId, setConversationId] = useState(null)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const [aiAction] = useMutation(AI_ACTION_MUTATION)
  const showQuickPrompts = messages.length === initialMessages.length && !isThinking

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [isOpen, isThinking, messages])

  useEffect(() => {
    if (isOpen && !isThinking && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen, isThinking])

  const resetConversation = () => {
    setMessages(initialMessages)
    setConversationId(null)
    setMessage('')
    setIsThinking(false)
  }

  const sendMessage = async (rawMessage) => {
    const trimmedMessage = rawMessage.trim()
    if (!trimmedMessage || isThinking) {
      return
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: trimmedMessage
    }

    setMessages((currentMessages) => currentMessages.concat(userMessage))
    setMessage('')
    setIsThinking(true)

    try {
      const { data } = await aiAction({
        variables: {
          message: trimmedMessage,
          conversationId: conversationId || undefined
        }
      })

      const response = parseAiActionResponse(data?.aiAction)
      if (response.conversationId) {
        setConversationId(response.conversationId)
      }

      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        text: response.text || 'I was not able to generate a response just now. Please try again.',
        actionLabel: response.actionLabel || null
      }

      setMessages((currentMessages) => currentMessages.concat(assistantMessage))
    } catch (error) {
      setMessages((currentMessages) => currentMessages.concat({
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        text: 'I ran into an error while reaching the AI assistant. Please try again.'
      }))
    } finally {
      setIsThinking(false)
    }
  }

  const onSubmit = async (event) => {
    event.preventDefault()
    await sendMessage(message)
  }

  return (
    <div className='calendar-ai-chat'>
      <div
        className={`calendar-ai-chat__panel${isOpen ? ' calendar-ai-chat__panel--open' : ''}`}
        role='dialog'
        aria-modal='false'
        aria-label='AI chat'
        aria-hidden={!isOpen}
      >
        <div className='calendar-ai-chat__header'>
          <div className='calendar-ai-chat__title-wrap'>
            <div className='calendar-ai-chat__title-icon-wrap'>
              <AssistantSparkIcon className='calendar-ai-chat__title-icon' />
            </div>
            <div className='calendar-ai-chat__title-copy'>
              <div className='calendar-ai-chat__title-line'>
                <span className='calendar-ai-chat__title'>Zinkerz Assistant</span>
                <span className='calendar-ai-chat__badge'>BETA</span>
              </div>
              <span className='calendar-ai-chat__status'>
                <span className='calendar-ai-chat__status-dot' />
                Online
              </span>
            </div>
          </div>

          <div className='calendar-ai-chat__header-actions'>
            <button
              type='button'
              className='calendar-ai-chat__header-action'
              onClick={resetConversation}
              aria-label='Start a new chat'
              title='New chat'
            >
              <NewChatIcon />
            </button>
            <button
              type='button'
              className='calendar-ai-chat__header-action'
              onClick={() => setIsOpen(false)}
              aria-label='Close AI chat'
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className='calendar-ai-chat__messages'>
          {messages.map((item) => (
            <React.Fragment key={item.id}>
              <div
                className={`calendar-ai-chat__row calendar-ai-chat__row--${item.role}`}
              >
                {item.role === 'assistant' && (
                  <div className='calendar-ai-chat__avatar'>
                    <AssistantSparkIcon className='calendar-ai-chat__avatar-icon' />
                  </div>
                )}

                <div className={`calendar-ai-chat__message calendar-ai-chat__message--${item.role}`}>
                  {item.text}
                </div>
              </div>

              {item.role === 'assistant' && item.actionLabel && (
                <button type='button' className='calendar-ai-chat__action-btn'>
                  {item.actionLabel}
                </button>
              )}
            </React.Fragment>
          ))}

          {showQuickPrompts && (
            <div className='calendar-ai-chat__chips'>
              {quickPrompts.map((prompt) => (
                <button
                  key={prompt}
                  type='button'
                  className='calendar-ai-chat__chip'
                  onClick={() => sendMessage(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}

          {isThinking && (
            <div className='calendar-ai-chat__thinking'>
              <span className='calendar-ai-chat__spinner' />
              Zinkerz Assistant is thinking...
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <form className='calendar-ai-chat__composer' onSubmit={onSubmit}>
          <input
            ref={inputRef}
            className='calendar-ai-chat__input'
            type='text'
            value={message}
            placeholder={isThinking ? 'Waiting for a reply...' : 'Ask the assistant anything...'}
            onChange={(event) => setMessage(event.target.value)}
            disabled={isThinking}
          />

          <button
            type='submit'
            className='calendar-ai-chat__send'
            disabled={!message.trim() || isThinking}
            aria-label='Send message'
          >
            <SendIcon />
          </button>
        </form>

        <div className='calendar-ai-chat__foot'>
          Zinkerz Assistant can make mistakes. Verify important details.
        </div>
      </div>

      <button
        type='button'
        className={`calendar-ai-chat__launcher${isOpen ? ' calendar-ai-chat__launcher--open' : ''}`}
        onClick={() => setIsOpen((currentValue) => !currentValue)}
        aria-label={isOpen ? 'Close AI chat' : 'Open AI chat'}
      >
        <span className='calendar-ai-chat__launcher-dot' />
        <AssistantSparkIcon className='calendar-ai-chat__launcher-icon calendar-ai-chat__launcher-icon--open' />
        <CloseIcon className='calendar-ai-chat__launcher-icon calendar-ai-chat__launcher-icon--close' />
      </button>

      {!isOpen && (
        <div className='calendar-ai-chat__tooltip'>Ask Zinkerz Assistant</div>
      )}
    </div>
  )
}

export default AIChat
